import React from 'react';
import PropTypes from 'prop-types';
import * as _ from 'lodash';
import fetchMg from '../utilities/fetchManager';
import Tippy from '../components/ReactTippy';
import InfinityScrollLoad from '../components/InfinityScrollLoad';
import { REQUEST_PARAMS } from '../utilities/constants';
import { messageUtility } from '../utilities/messageUtility';
import { ArrowDownIcon } from '../components/icons/ArrowDownIcon';

import { FormDropdownClearButton } from '../components/CommonFormComponents';
import { RadioButtonIcon } from '../components/icons/RadioButtonIcon';

// ドロップダウンリストが呼び出し元で複数表示できるので一度取得した結果は共有する
export class ApiCache {
  constructor() {
    this.resultMap = {};
  }

  apiCall = async (apiCallF, params) => {
    // paramsは深い階層はない前提
    const key = _.join(_.map(params, p => p).join(), ',');
    const cacheValue = this.resultMap[key];
    if (cacheValue) {
      return cacheValue;
    } else {
      const result = await apiCallF();
      this.resultMap[key] = result;
      return result;
    }
  };
}

export const formatForemanName = foreman => {
  const primaryPartnerCompanyName = _.isNil(
    foreman.primary_partner_company_name
  )
    ? foreman.primary_partner_company_name
    : '';
  const companyName = !_.isNil(foreman.company_name)
    ? foreman.company_name
    : '';
  const fullName = !_.isNil(foreman.fullname) ? foreman.fullname : '';

  if (!primaryPartnerCompanyName && !companyName && !fullName) {
    return '';
  }

  return `${primaryPartnerCompanyName} ${
    primaryPartnerCompanyName ? `(${companyName}) ` : `${companyName}`
  }${foreman.private_partner_company_flag ? `(別途業者) ` : ''}(${fullName})`;
};

/**
 * エラーメッセージの取得
 *
 * @param {*} errors
 * @param {*} column
 * @returns
 */
export const getErrorMessage = (errors, column) => {
  return errors[column] ? errors[column] : null;
};

/**
 * ドロップダウンの共通パーツ
 *
 * @export
 * @class DropdownSelect
 * @param {string} label 未選択時のデフォルトラベル
 * @param {string} apiPath APIのパス
 * @param {string} entitiesKey API結果のitemの配列のキー
 * @param {string} itemKey API結果のitemの配列内itemの表示したい対象のキー(デフォルト: 'name')
 * @param {string} searchKey 検索のparamとなるキー(デフォルト: 'name')
 * @param {string} setKey ragioボタン、ラベル押下時に選択状態とするkey(デフォルト: 'id')
 * @param {object} value 値・ID
 * @param {object} valueName 値・名前
 * @param {object} requiredParams APIの必須param
 * @extends {React.Component}
 */
export class DropdownSelect extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      show: false,
      list: [],
      page: 0, // offset用
      selected: {
        [this.props.setKey]: this.props.value,
      },
      searchWord: '',
      foremanLabel: this.props.label,
      isLoading: false, // ロード中フラグ
    };
    this.serchMinLength = this.props.serchMinLength
      ? this.props.serchMinLength
      : 0;
    this.parent = React.createRef();
    this.tippyRef = React.createRef();
    this.firstLoadFlag = false; // 初回ロードフラグ
    this.uniqueId = _.uniqueId();

    this.targetId = `infinityscroll-target-dropdown-${this.uniqueId}`;
  }

  componentDidUpdate() {
    if (this.state.show === true) {
      // ドロップダウンが可視状態になった場合は、documentにコンポーネント外をclickしたかのイベントを追加する
      document.addEventListener(
        'click',
        this.handleClickOutsideOfComponent,
        false
      );
    }
    if (this.state.show === false) {
      // ドロップダウンが不可視状態になった場合は、documentのclickイベントを削除する
      document.removeEventListener(
        'click',
        this.handleClickOutsideOfComponent,
        false
      );
    }
  }

  componentWillUnmount() {
    document.removeEventListener(
      'click',
      this.handleClickOutsideOfComponent,
      false
    );
  }

  handleClickOutsideOfComponent = events => {
    // コンポーネント内クリックの場合は無視する
    if (
      !_.isNull(this.parent.current) &&
      this.parent.current.contains(events.target)
    ) {
      return;
    }
    // コンポーネント外クリックの場合はドロップダウンを不可視とする
    // イベントはstateにfalseが設定された際にremoveされる
    this.setState({ show: false });
  };

  /**
   * ドロップダウンがオープン・クローズされる時
   *
   * @memberof DropdownSelect
   */
  handleShowForm() {
    //ie対応 Tippyを先に非表示にしないとドロップダウンが正しく表示されない
    if (this.tippyRef) {
      this.tippyRef.current.hide(0); //0にして即時に閉じるようにする必要がある
    }
    if (!this.firstLoadFlag) {
      this.initSearch();
    }
    this.setState({ show: true });
  }

  /**
   * 検索
   *
   * @param {*} value
   * @memberof DropdownSelect
   */
  onChangeSearchWord = _.debounce(value => {
    // テキストボックスに入力する度に連続で呼ばれるのでdebounceで抑制する
    this.setState(
      {
        searchWord: value,
        list: [],
        page: 0,
        loaded: false,
        isLoading: true,
      },
      () => {
        this.updateList(this.state.searchWord);
      }
    );
  }, 500);

  initSearch = () => {
    this.setState(
      {
        loaded: false,
        isLoading: true,
      },
      () => {
        this.updateList();
      }
    );
    this.firstLoadFlag = true;
  };

  handleInview = () => {
    if (this.state.loaded && !this.state.allLoaded) {
      this.setState(
        {
          loaded: false,
          isLoading: true,
        },
        () => {
          this.updateList(this.state.searchWord);
        }
      );
    }
  };

  /**
   * [変更]ボタンの押下
   *
   * @memberof DropdownSelect
   */
  onClickSelect() {
    this.props.changeEvent(this.props.name, this.state.selected);
    // 開いたのを閉じる
    this.setState({
      show: false,
    });
  }

  /**
   * ラジオボタンや右のラベルクリック時
   *
   * @param {*} val
   * @memberof DropdownSelect
   */
  onClickRadio(val) {
    if (this.state.selected.id === val.id) {
      this.setState({ selected: {} });
    } else {
      this.setState({ selected: val });
    }
  }

  apiCall = async (apiPath, params) => {
    const callF = () =>
      fetchMg.GetAjax(apiPath, params).catch(() => ({ result: false }));
    if (this.props.apiCache) {
      return this.props.apiCache.apiCall(callF, params);
    }
    return callF();
  };

  /**
   * 一覧の更新
   *
   * @param {*} searchWord
   * @memberof DropdownSelect
   */
  async updateList(searchWord = '') {
    if (this.state.loaded) {
      return;
    }

    let list = [];
    const name = searchWord.length >= this.serchMinLength ? searchWord : '';
    if (this.props.apiPath) {
      const apiPath = this.props.apiPath;
      let params = {
        [this.props.searchKey || 'name']: name,
        offset: _.size(this.state.list),
        limit: REQUEST_PARAMS.parentLimit,
      };

      if (this.props.requiredParams) {
        params = _.assign(params, this.props.requiredParams);
      }

      const data = await this.apiCall(apiPath, params);
      list = data.data.entities[this.props.entitiesKey];

      // const allLoadedLimit = this.props.limit_by_first_companies
      //   ? 1
      //   : params.limit;
      /**
       * 協力会社のパラメーターにlimit_by_first_companiesがあった時の設定
       * 不必要と判断出来れば他と動作を揃える
       *
       * 関連：BLDE_V2-20794、BLDE_V2-18279
       */
      const allLoadedLimit = 1;

      // APIからの追加データがなかった場合、_.size(list)は0になる
      const allLoaded = _.size(list) < allLoadedLimit;
      const newList = this.state.list.concat(list);
      this.setState({
        list: newList,
        allLoaded,
        page: this.state.page + 1,
        isLoading: false,
        loaded: true,
      });
    } else {
      list = this.props.list;
      if (searchWord !== '') {
        list = _.filter(list, item => {
          return item[this.props.valueName].indexOf(searchWord) >= 0;
        });
      }
      this.setState({ list, isLoading: false });
    }
  }

  clearSelectedRow = () => {
    this.setState(
      {
        selected: {
          [this.props.setKey]: null,
        },
      },
      () => {
        this.onClickSelect();
      }
    );
  };

  /**
   * ラジオボタンのラベルの一覧
   *
   * @returns
   * @memberof DropdownSelect
   */
  setItems() {
    const setKey = this.props.setKey || 'id';
    return _.map(this.state.list, (item, index) => {
      // ラベルクリックでラジオボタン選択のためにid作成
      const id = `dropdown-select-${this.uniqueId}-${index}-${item[setKey]}`;
      const label = this.props.itemNameFormatter
        ? this.props.itemNameFormatter(item)
        : item[this.props.itemKey] || 'name';

      return (
        <li
          key={item[setKey]}
          className={`single-select__item ${
            !_.isUndefined(this.props.itemWidthClass)
              ? this.props.itemWidthClass
              : ''
          }`}
        >
          <label className='radio'>
            <input
              id={id}
              type='radio'
              checked={this.state.selected[setKey] === item[setKey]}
              onChange={() => {
                if (this.state.selected[setKey] !== item[setKey]) {
                  this.setState({ selected: item }, () => {
                    this.onClickSelect();
                  });
                }
              }}
            />
            <RadioButtonIcon className='icon--1_6x' />
            {/* tooltip */}
            <Tippy
              duration={10}
              arrow={true}
              arrowType='sharp'
              placement='bottom'
              role={`tippy_target_${id}`}
              html={renderTooltipContent(label)}
              onShow={setTippyHide}
            >
              <div className={`single-select__label tippy_target_${id}`}>
                {/* ラベルクリックでラジオボタン選択のため */}
                <label htmlFor={id}>{label}</label>
              </div>
            </Tippy>
          </label>
        </li>
      );
    });
  }

  render() {
    // 未選択のメッセージまたは選んだ文字
    const getDropdownLabel = () => {
      let label = `${this.props.label}`;
      if (this.props.valueName) {
        label = this.props.valueName;
      }
      return label;
    };

    return (
      <div className='dropdown-single-select dropdownselect' ref={this.parent}>
        <div
          className={`dropdown-single-select__header ${
            this.state.show ? 'show' : ''
          }`}
        >
          <Tippy
            duration={10}
            multiple={false}
            arrow={true}
            arrowType='sharp'
            placement='bottom'
            maxWidth='160px'
            role={`tippy_target_${this.uniqueId}`}
            html={renderTooltipContent(getDropdownLabel())}
            ref={this.tippyRef}
          >
            <div
              className={`dropdown-select`}
              onClick={() => this.handleShowForm()}
            >
              <span className='dropdown-select__txt'>{getDropdownLabel()}</span>
            </div>
            <ArrowDownIcon className='icon--2_4x form-list__arrow-icon' />
          </Tippy>
        </div>
        {this.state.show && (
          <div className={'dropdown dropdown--dropdown-list show'}>
            <div
              className={`dropdown__item ${
                !_.isUndefined(this.props.widthClass)
                  ? this.props.widthClass
                  : ''
              } dropdown__item--hidden`}
            >
              <div className='dropdown-single-select__item'>
                <i className='i-search i-2x' />
                <input
                  type='text'
                  placeholder={messageUtility.getMessage('I-000-090')}
                  defaultValue={this.state.searchWord}
                  onChange={element =>
                    this.onChangeSearchWord(element.target.value)
                  }
                />
              </div>
              {this.props.isOptional && (
                <div className='dropdown-single-select__item'>
                  <FormDropdownClearButton onClick={this.clearSelectedRow} />
                </div>
              )}
              <div
                className='dropdown-single-select__scroll'
                id={this.targetId}
              >
                <ul className='single-select'>
                  {this.setItems()}
                  <InfinityScrollLoad
                    targetId={this.targetId}
                    total={_.size(this.state.list)}
                    isLoading={this.state.isLoading}
                    callback={this.handleInview}
                    ignoreIndicator={true}
                  />
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

/**
 * Tooltipで表示される文字
 * @param data {string}
 */
const renderTooltipContent = data => {
  const rowStyle = {
    maxWidth: '240px',
    wordWrap: 'break-word',
    color: '#FFF',
    fontSize: '14px',
    lineHeight: '1.5',
    textAlign: 'left',
  };

  return (
    <React.Fragment>
      <div style={rowStyle}>{data}</div>
    </React.Fragment>
  );
};

/**
 * tooltip表示判定
 * @param {object} instance // tippy
 */
const setTippyHide = instance => {
  const targetOption = document.getElementsByClassName(
    `${instance.options.role}`
  );
  if (targetOption && targetOption[0]) {
    instance.popper.hidden = !(
      targetOption[0].offsetWidth < targetOption[0].scrollWidth
    );
  }
};

DropdownSelect.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  apiPath: PropTypes.string.isRequired,
  entitiesKey: PropTypes.string.isRequired,
  itemKey: PropTypes.string.isRequired,
  searchKey: PropTypes.string,
  setKey: PropTypes.string,
  value: PropTypes.number,
  valueName: PropTypes.string,
  large: PropTypes.string,
  requiredParams: PropTypes.object,
  itemNameFormatter: PropTypes.func,
  apiCache: PropTypes.object,
};



// WEBPACK FOOTER //
// ./src/pages/GroupSettingCommon.jsx